import clone from "clone";
import React from "react";
import { surveyHelper, SurveySegment, surveySegmentCompare, surveySegmentListCompare, ScoredSelectionStats, ISelectionStats, SurveyReportBuilder, SurveyQuestion, Survey, SurveyReport } from "../../../../../../../amorg-common";
import { SelectField } from "../../../../../../../designSystem/formField/SelectField";
import { SegmentStatsComparisonGraph } from "../../../../../../../designSystem/graphs/SegmentStatsComparisonGraph";
import { SelectOption } from "../../../../../../../designSystem/inputs/Select";
import { P } from "../../../../../../../designSystem/typography/Body";
import { Em } from "../../../../../../../designSystem/typography/Em";
import { WorkplaceAssessmentComponentProps } from "../../_components/WorkplaceAssessmentComponentProps";
import { PrintButtonWithContent } from "../../../../../../_components/PrintButtonWithContent";
import { PageSection } from "../../../../../../../designSystem/PageSection";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";

type ComparisonReportPanelProps = WorkplaceAssessmentComponentProps & 
    { 
        survey:Survey, // non-nullable version
        surveyReport:SurveyReport, // non-nullable version
        setHeaderButtons: React.Dispatch<React.SetStateAction<React.ReactNode>>,
};

type AggregatedDataSet = {
    type:'all' | 'theme' | 'indicator', title:string, data:{segments:SurveySegment[], stats:ScoredSelectionStats }[]
}
type QuestionDataSet = {
    type:'question', question:SurveyQuestion, data:{ segments:SurveySegment[], stats:ISelectionStats }[]
}
type DataSet = AggregatedDataSet | QuestionDataSet;



export function ComparisonReportPanel(props:ComparisonReportPanelProps):JSX.Element
{
    return (
        <InnerComparisonPanel {...props}
            selectedComparison={''}
        />
    );
}



function InnerComparisonPanel(props:
    ComparisonReportPanelProps & {
    selectedComparison:string
}):JSX.Element
{
    // SelectedComparison format:
    // '' = all questions
    // 'indicator_<indicator>' = all questions with the given indicator
    // 'theme_<themeIndex>' = all questions in the given theme
    // 'question_<themeIndex>_<questionIndex>' = the given question
    const [selectedComparison, setSelectedComparison] = React.useState<string>(props.selectedComparison);
    
    const comparisonTypeOptions:SelectOption[] = React.useMemo(()=>{
        const options = [
            { value:'', text:'Alle spørgsmål' }
        ];

        const indicators = surveyHelper.getAllIndicators(props.survey);
        for(let i=0; i < indicators.length; i++)
        {
            options.push({ value:`indicator_${indicators[i]}`, text:`Indikator ${i+1}: ${indicators[i]}` });
        }

        for(let i=0; i < props.survey.themes.length; i++)
        {
            options.push({ value:`theme_${i}`, text:`Tema ${i+1}: ${props.survey.themes[i].name}` });
            for(let j=0; j < props.survey.themes[i].questions.length; j++)
            {
                options.push({ value:`question_${i}_${j}`, text:`Spørgsmål ${i+1}.${j+1}: ${props.survey.themes[i].questions[j].title}` });
            }
        }

        const maxLength = 100;
        options.filter(o => {
            const textLength = o.text.length;
            if(textLength > maxLength)            {
                o.text = o.text.substring(0, maxLength - 3) + '...';
            }
        });

        return options;
    }, [props.survey, props.surveyReport]);

    const dataSet:DataSet|undefined = React.useMemo(() => {
        
        if(!props.surveyReport)
            return undefined;

        let data:DataSet|undefined = undefined;
        if(!selectedComparison)
        {
            data = getOverviewDataSet();
        }
        else if(selectedComparison.startsWith('indicator'))
        {
            const indicator = selectedComparison.split('_')[1];
            data = getIndicatorDataSet(indicator);
        }
        else if(selectedComparison.startsWith('theme'))
        {
            const themeIndex = parseInt(selectedComparison.split('_')[1]);
            data = getThemeDataSet(themeIndex);
        }
        else if(selectedComparison.startsWith('question'))
        {
            const parts = selectedComparison.split('_');
            const themeIndex = parseInt(parts[1]);
            const questionIndex = parseInt(parts[2]);
            data = getQuestionDataSet(themeIndex, questionIndex);
        }
        else
            throw new Error(`Unknown comparison type: ${selectedComparison}`);

        const sortedDataSetData = clone(data);
        for(const sr of sortedDataSetData.data){
            sr.segments.sort((a, b) => surveySegmentCompare(props.survey.segmentations, a, b));
        }
        sortedDataSetData.data.sort((a, b) => surveySegmentListCompare(props.survey.segmentations, a.segments, b.segments));

        return sortedDataSetData;



        function getOverviewDataSet():DataSet
        {
            const data = props.surveyReport.selectionReports.map(sr => 
            {
                const reportBuilder = new SurveyReportBuilder(props.survey, props.surveyReport);
                const data = reportBuilder.getData([sr.segments]);
                return {
                    segments:sr.segments,
                    stats: data.stats
                }
            });
            return {
                type:'all',
                title:'Overview',
                data
            }
        }

        function getThemeDataSet(themeIndex:number):DataSet
        {
            const data = props.surveyReport.selectionReports.map(sr => 
            {
                const reportBuilder = new SurveyReportBuilder(props.survey, props.surveyReport);
                const data = reportBuilder.getData([sr.segments]);
                return {
                    segments:sr.segments,
                    stats: data.themeData[themeIndex].stats,
                }
            });
            return {
                type:'theme',
                title:props.survey.themes[themeIndex].name,
                data
            }
        }

        function getIndicatorDataSet(indicator:string):DataSet
        {
            const data = props.surveyReport.selectionReports.map(sr => 
            {
                const reportBuilder = new SurveyReportBuilder(props.survey, props.surveyReport);
                const data = reportBuilder.getData([sr.segments]);
                const indicatorSelections = data.indicatorData.find(ir => ir.indicator === indicator)?.stats;
                if(!indicatorSelections)
                    throw new Error(`Indicator ${indicator} not found in report data`);

                return {
                    segments:sr.segments,
                    stats: indicatorSelections,
                }
            });
            return {
                type:'indicator',
                title:indicator,
                data
            }
        }

        function getQuestionDataSet(themeIndex:number,questionIndex:number):DataSet
        {
            const data =  props.surveyReport.selectionReports.map(sr => 
            {
                const reportBuilder = new SurveyReportBuilder(props.survey, props.surveyReport);
                const data = reportBuilder.getData([sr.segments]);
                const stats = data.themeData[themeIndex].questions[questionIndex].stats;

                return {
                    segments:sr.segments,
                    stats,
                }
            });

            return {
                type:'question',
                question:props.survey.themes[themeIndex].questions[questionIndex],
                data
            }
        }

    }, [props.survey, props.surveyReport, props.selectedComparison, selectedComparison]);

    const validDisplay = dataSet && dataSet.data.length > 0 && surveyHelper.getAllSurveyQuestions(props.survey).length > 0;

    React.useEffect(() => {
        if(!validDisplay)
        {
            props.setHeaderButtons(undefined);
            return;
        }

        props.setHeaderButtons(
            <PrintButtonWithContent documentTitle={`${props.activity.title} - Sammenligning`}>
                {() =>
                    <ComparisonReportContent
                        dataSet={dataSet}
                    />
                }
            </PrintButtonWithContent>
        );
    }, [dataSet, props.setHeaderButtons, props.activity.title, selectedComparison, selectedComparison, validDisplay]);

    React.useEffect(() => {
        return () => props.setHeaderButtons(undefined);
    }, [props.setHeaderButtons]);

    return (

        !validDisplay
        ? <P>Der er ingen data at vise i rapporten</P>
        :
            <>
                <Flex>
                    <SelectField
                        minWidth='300px'
                        label='Sammenligningsgrundlag'
                        options={comparisonTypeOptions}
                        selectedValue={selectedComparison || ''}
                        onChange={(val) => setSelectedComparison(val)}
                        toolTip={
                            <div>Rapporten viser gennemsnitsscoren for det valgte sammenligningsgrundlag, opdelt på tværs af de segmenter, som rapporten indeholder.
                            Scoren er udregnet som procentdelen af respondenter, der har svaret enig eller meget enig på spørgsmålene, der indgår i det valgte sammenligningsgrundlag.</div>
                        } 
                    />
                </Flex>

                <ComparisonReportContent
                    dataSet={dataSet}
                />
            </>           
    );
}

type ComparisonReportContentProps = {
    dataSet: DataSet,
};

function ComparisonReportContent(props: ComparisonReportContentProps): JSX.Element
{
    return (
        <PageSection contentStyleVariant="card">

            {props.dataSet.type === 'all' &&
                <div>
                    Fordeling af besvarelser for <Em>alle spørgsmål</Em> grupperet efter segment
                </div>

            }

            {props.dataSet.type === 'indicator' &&
                <div>
                    Fordeling af besvarelser for spørgsmål med indikatoren <Em>{props.dataSet.title}</Em> grupperet efter segment
                </div>
            }

            {props.dataSet.type === 'theme' &&
                <div>
                    Fordeling af besvarelser for spørgsmål i temaet <Em>{props.dataSet.title}</Em> grupperet efter segment
                </div>
            }

            {props.dataSet.type === 'question' &&
                <div>
                    Fordeling af besvarelser for spørgsmålet <Em>{props.dataSet.question.title}</Em> grupperet efter segment
                </div>
            }

            {props.dataSet.data.length === 0 && (
                <div>Ingen data tilgængelig</div>
            )}

            

            {props.dataSet.type==='all' && props.dataSet.data.length > 0 &&
                <SegmentStatsComparisonGraph
                    type='all'
                    data={props.dataSet.data.map(d => {
                        return {
                            segments:d.segments,
                            stats:d.stats as ScoredSelectionStats
                        }
                    }
                    )}
                />
            }

            {props.dataSet.type==='indicator' && props.dataSet.data.length > 0 &&
                <SegmentStatsComparisonGraph
                    type='indicator'
                    data={props.dataSet.data.map(d => {
                        return {
                            segments:d.segments,
                            stats:d.stats as ScoredSelectionStats
                        }
                    }
                    )}
                />
            }

            {props.dataSet.type==='theme' && props.dataSet.data.length > 0 &&
                <SegmentStatsComparisonGraph
                    type='theme'
                    data={props.dataSet.data.map(d => {
                        return {
                            segments:d.segments,
                            stats:d.stats as ScoredSelectionStats
                        }
                    })} 
                />
            }

            {props.dataSet.type==='question' && props.dataSet.data.length > 0 &&
                <SegmentStatsComparisonGraph
                    type='question'
                    question={props.dataSet.question}
                    data={props.dataSet.data.map(d => {
                        return {
                            segments:d.segments,
                            stats:d.stats
                        }
                    })} 
                />
            }
        </PageSection>
    );
}